import { Button, useDisclosure } from "@heroui/react";
import { useEffect, useState, useCallback } from "react";
import { api } from "@/api";
import { LockPasscode } from "@/api/locks/locks";
import { toast } from "react-toastify";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { FaPencil, FaPlus } from "react-icons/fa6";
import { CreatePasscodeModal } from "@/app/components/modals/lock/createPasscodeModal";
import { EditPasscodeModal } from "@/app/components/modals/lock/editPasscodeModal";
import useErrorHandling from "@/hooks/useErrorHandling";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { setSelectedLock } from "@/app/store/locks/reducer";
import { NewLockHandbook } from "@/app/components/handbooks/lockHandbook";
import { skudLinks, Links } from "@/app/components/navigation/links/Links";
import { useLocation } from "react-router-dom";
import { OfficeLock } from "@/api/locks/locks";

export function SkudPasscodesPage() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const selectedLockId = useAppSelector((state) => state.locks.selectedLockId);

  const [lock, setLock] = useState<OfficeLock | null>(null);
  const [passcodes, setPasscodes] = useState<LockPasscode[] | null>(null);
  const [passcode, setPasscode] = useState<LockPasscode | null>(null);

  const handleError = useErrorHandling();

  const createPasscodeModalDisclosure = useDisclosure();
  const editPasscodeModalDisclosure = useDisclosure();

  const fullscreenDisclosure = useDisclosure();

  const editPasscode = (passcode: LockPasscode) => {
    setPasscode(passcode);
    editPasscodeModalDisclosure.onOpen();
  };

  const handleUnlock = useCallback(() => {
    if (!selectedLockId) {
      toast.error("Выберите замок");
      return;
    }

    api.locks
      .unlock(selectedLockId)
      .then(() => toast.success("Замок успешно открыт!"))
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [selectedLockId, handleError]);

  useEffect(() => {
    if (!selectedLockId) {
      setLock(null);
      setPasscodes(null);
      return;
    }

    api.locks
      .get(selectedLockId)
      .then((response) => {
        setLock(response.data);
      })
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });

    api.locks
      .passcodes(selectedLockId)
      .then((response) => {
        setPasscodes(response.data);
      })
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [
    selectedLockId,
    createPasscodeModalDisclosure.isOpen,
    editPasscodeModalDisclosure.isOpen,
    handleError,
  ]);

  return (
    <>
      <CreatePasscodeModal
        disclosure={createPasscodeModalDisclosure}
        lock={selectedLockId}
      />
      <EditPasscodeModal
        disclosure={editPasscodeModalDisclosure}
        lock={selectedLockId || 0}
        passcode={passcode}
      />
      <div className="flex flex-col md:flex-row gap-4 w-full">
        <div className="flex flex-col gap-2 w-full md:min-w-[240px] md:w-auto">
          <NewLockHandbook
            errorMessage=""
            isInvalid={false}
            onSelectionChange={(key: any) =>
              dispatch(setSelectedLock(Number(key)))
            }
            selectedKey={selectedLockId ? String(selectedLockId) : undefined}
            children={<></>}
          />
          <Links
            links={skudLinks(location.pathname, !!selectedLockId, handleUnlock)}
          />
        </div>
        <div className="flex-grow flex flex-col gap-2 w-full">
          <div className="w-full flex flex-col gap-4 bg-white dark:bg-zinc-900 p-4 rounded-xl border-2 border-foreground-200 dark:border-foreground-100">
            {!selectedLockId && (
              <div className="flex flex-col items-center justify-center p-8">
                <span className="text-foreground-500">
                  Выберите замок для управления паролями
                </span>
              </div>
            )}
            {selectedLockId && (
              <>
                <div className="flex flex-row w-full items-center">
                  <div className="flex flex-row w-full gap-4 items-center">
                    <div className="flex flex-row items-center gap-2">
                      <span className="font-medium">
                        Замок {lock?.display_name}
                      </span>
                    </div>
                    <Button
                      startContent={<FaPlus />}
                      size="sm"
                      color="primary"
                      variant="solid"
                      className="max-w-fit"
                      onClick={() => createPasscodeModalDisclosure.onOpen()}>
                      Создать пароль
                    </Button>
                  </div>
                </div>
                <TableBuilder
                  fullscreenDisclosure={fullscreenDisclosure}
                  removeWrapper
                  displayOptions={false}
                  columns={[
                    {
                      label: "Пароль",
                      key: "passcode",
                      type: ColumnType.String,
                    },
                    {
                      key: "actions",
                      label: "Действия",
                      type: ColumnType.Actions,
                      actions: [
                        {
                          icon: <FaPencil />,
                          onClick: (_value, row) => {
                            editPasscode(row);
                          },
                        },
                      ],
                    },
                  ]}
                  data={passcodes || []}
                  rowsPerPage={10}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
